import { ERROR_CONFIG } from '../config';

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  userAgent: string;
  url: string;
  userId?: string | null;
  errorId: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
}

interface ErrorReportingConfig {
  enabled: boolean;
  apiEndpoint?: string;
  apiKey?: string;
  maxRetries: number;
  batchSize: number;
  flushInterval: number; // milliseconds
}

class ErrorReportingService {
  private config: ErrorReportingConfig;
  private errorQueue: ErrorReport[] = [];
  private flushTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<ErrorReportingConfig> = {}) {
    this.config = {
      enabled: ERROR_CONFIG.REPORTING.ENABLED,
      maxRetries: ERROR_CONFIG.REPORTING.MAX_RETRIES,
      batchSize: ERROR_CONFIG.REPORTING.BATCH_SIZE,
      flushInterval: ERROR_CONFIG.REPORTING.FLUSH_INTERVAL,
      ...config
    };

    if (this.config.enabled) {
      this.startFlushTimer();
    }
  }

  /**
   * Report an error to the error reporting service
   */
  reportError(
    error: Error, 
    context: {
      componentStack?: string;
      userId?: string | null;
      severity?: 'low' | 'medium' | 'high' | 'critical';
      additionalContext?: Record<string, any>;
    } = {}
  ): string {
    const errorId = this.generateErrorId();
    
    const errorReport: ErrorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: context.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: context.userId || this.getCurrentUserId(),
      errorId,
      severity: context.severity || 'medium',
      context: context.additionalContext
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      this.logToConsole(errorReport);
    }

    // Queue for reporting if enabled
    if (this.config.enabled) {
      this.queueError(errorReport);
    }

    return errorId;
  }

  /**
   * Report a custom message (not from an Error object)
   */
  reportMessage(
    message: string,
    severity: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    context: Record<string, any> = {}
  ): string {
    const errorId = this.generateErrorId();
    
    const errorReport: ErrorReport = {
      message,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      errorId,
      severity,
      context
    };

    if (process.env.NODE_ENV === 'development') {
      this.logToConsole(errorReport);
    }

    if (this.config.enabled) {
      this.queueError(errorReport);
    }

    return errorId;
  }

  /**
   * Flush all queued errors immediately
   */
  async flush(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    try {
      await this.sendErrors(errors);
    } catch (error) {
      console.error('Failed to flush errors:', error);
      // Re-queue errors for retry
      this.errorQueue.unshift(...errors);
    }
  }

  /**
   * Set configuration options
   */
  configure(config: Partial<ErrorReportingConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (this.config.enabled && !this.flushTimer) {
      this.startFlushTimer();
    } else if (!this.config.enabled && this.flushTimer) {
      this.stopFlushTimer();
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): ErrorReportingConfig {
    return { ...this.config };
  }

  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getCurrentUserId(): string | null {
    try {
      // Try to get current user from Firebase Auth
      const auth = require('firebase/auth').getAuth();
      return auth.currentUser?.uid || null;
    } catch {
      return null;
    }
  }

  private logToConsole(errorReport: ErrorReport): void {
    const severity = errorReport.severity.toUpperCase();
    const prefix = `🚨 [${severity}] Error Report`;
    
    console.group(prefix);
    console.error('Message:', errorReport.message);
    console.error('Error ID:', errorReport.errorId);
    console.error('Timestamp:', errorReport.timestamp);
    
    if (errorReport.stack) {
      console.error('Stack:', errorReport.stack);
    }
    
    if (errorReport.componentStack) {
      console.error('Component Stack:', errorReport.componentStack);
    }
    
    if (errorReport.context) {
      console.error('Context:', errorReport.context);
    }
    
    console.error('Full Report:', errorReport);
    console.groupEnd();
  }

  private queueError(errorReport: ErrorReport): void {
    this.errorQueue.push(errorReport);
    
    // Flush immediately if queue is full
    if (this.errorQueue.length >= this.config.batchSize) {
      this.flush();
    }
  }

  private async sendErrors(errors: ErrorReport[]): Promise<void> {
    if (!this.config.apiEndpoint) {
      // If no API endpoint configured, just log that we would send
      console.log(`Would send ${errors.length} error reports to external service`);
      return;
    }

    const response = await fetch(this.config.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.apiKey && { 'Authorization': `Bearer ${this.config.apiKey}` })
      },
      body: JSON.stringify({ errors })
    });

    if (!response.ok) {
      throw new Error(`Failed to send errors: ${response.status} ${response.statusText}`);
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      if (this.errorQueue.length > 0) {
        this.flush();
      }
    }, this.config.flushInterval);
  }

  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }
}

// Create singleton instance
const errorReportingService = new ErrorReportingService();

// Export both the class and the singleton
export { ErrorReportingService };
export default errorReportingService;

// Export utility functions for common use cases
export const reportError = (
  error: Error, 
  context?: Parameters<typeof errorReportingService.reportError>[1]
): string => {
  return errorReportingService.reportError(error, context);
};

export const reportMessage = (
  message: string,
  severity?: Parameters<typeof errorReportingService.reportMessage>[1],
  context?: Parameters<typeof errorReportingService.reportMessage>[2]
): string => {
  return errorReportingService.reportMessage(message, severity, context);
};

export const configureErrorReporting = (
  config: Partial<ErrorReportingConfig>
): void => {
  errorReportingService.configure(config);
};

// Ensure errors are flushed before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    errorReportingService.flush();
  });
}